import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Button,
  Drawer,
  Grid,
  Snackbar,
  Tooltip,
} from '@mui/material'
import { createColumnHelper } from '@tanstack/react-table'
import { clientsAPI } from 'api/clientsAPI'
import { User, UserProfiles } from 'api/models/User.type'
import confirmationAlert from 'components/Common/ConfirmAlert/ConfirmAlert'
import SectionHeader from 'components/Common/SectionHeader'
import TypeScriptTable from 'components/Common/Tables/TypeScriptTable/TypeScriptTable'
import useClientUsers from 'hooks/useClientUsers'
import useDebounce from 'hooks/useDebounce'
import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AddUserToProfile } from './AddUserToProfile'

type UserProfileProps = {
  client: any
}

const pageHeader = 'User Profiles'
const pageInfo =
  'Please contact your systems administrator to set up new profiles.'

const UserProfile = ({ client }: UserProfileProps) => {
  const [showAlert, setShowAlert] = useState(false)
  const [alertType, setAlertType] = useState<AlertColor>()
  const [alertMessage, setAlertMessage] = useState('')

  const [openClientUserProfileDrawer, setOpenClientUserProfileDrawer] =
    React.useState(false)

  //get the client profiles
  const {
    data: results,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['userProfiles', client.id],
    queryFn: () => clientsAPI.getUserProfiles(client.id),
    enabled: !!client.id,
  })

  if (isLoading) {
    console.log('Loading user profiles...')
  }

  if (error) {
    console.error('Error fetching user profiles:', error)
  }

  const userProfiles = results?.data?.value ?? []

  const deleteUserFromProfile = async (userId: number, profileId: number) => {
    console.log('userId', userId)
    console.log('profileId', profileId)
    const userProfile: UserProfiles | undefined = userProfiles.find(
      (profile: UserProfiles) => profile.id === profileId,
    )
    if (userProfile) {
      userProfile.users = (userProfile.users as User[]).filter(
        (user) => user.userId !== userId,
      )
    }

    const updatededUserProfile: any = {
      ...userProfile,
      users: userProfile?.users.map((user) => user.userId),
    }

    console.log(
      `User with ID ${userId} removed from profile: `,
      updatededUserProfile,
    )
    confirmationAlert({
      title: 'Remove User',
      action: async function (): Promise<void> {
        try {
          if (updatededUserProfile) {
            await clientsAPI.updateClientUserProfile(
              client.id,
              profileId,
              updatededUserProfile,
            )
          } else {
            throw new Error('User profile not found.')
          }
          setShowAlert(true)
          setAlertType('success')
          setAlertMessage('User removed from profile')
          refetch()
        } catch (error) {
          setShowAlert(true)
          setAlertType('error')
          setAlertMessage(`No updates made: ${error.message}`)

          console.error(
            `Error removing user with ID ${userId} from profile ${profileId}:`,
            error,
          )
        }
        refetch()
      },
      confirmationMessage: 'The action will remove the user from the profile.',
      confirmationQuestion: 'Are you sure you want to proceed?',
    })
  }

  //get the client Users
  const { users: clientUsers } = client.id
    ? useClientUsers(client.id)
    : { users: [] }

  const columnHelper = createColumnHelper<User>()
  const tableColumns = [
    columnHelper.accessor(
      (row) => {
        console.log(row)
        return `${row.lastName}, ${row.firstName} (${row.email})`
      },
      {
        id: 'User',
      },
    ),
    columnHelper.display({
      id: 'Actions',
      cell: ({ row }) => (
        <Box
          component="span"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          title="Remove user from profile"
          onClick={() => {
            console.log(`Remove user with ID: ${row.original}`)
            // Add your remove logic here
            deleteUserFromProfile(row.original.userId, row.original.profileId)
          }}
        >
          <Tooltip title="Remove user from profile">
            <CloseIcon sx={{ color: 'red' }} />
          </Tooltip>
        </Box>
      ),
    }),
  ]

  return (
    <Box component={'div'}>
      <Grid container>
        <Grid item xs={12} sx={{ marginTop: '20px' }}>
          <SectionHeader
            title={`${pageHeader} for ${client.companyName}`}
            subtitle={pageInfo}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenClientUserProfileDrawer(true)}
          >
            Add User to Profile
          </Button>
        </Grid>
        <Grid item xs={8} sx={{ marginTop: '40px' }}>
          {userProfiles.map((profile: UserProfiles) => {
            profile.users = profile.users.map((user) => ({
              ...user,
              profileId: profile.id,
            }))
            return (
              <Box key={profile.id} component={'div'} sx={{ marginBottom: 4 }}>
                <SectionHeader
                  title={profile.name}
                  subtitle={profile.description}
                />
                <TypeScriptTable
                  data={profile.users}
                  isLoading={isLoading}
                  columns={tableColumns}
                />
              </Box>
            )
          })}
        </Grid>
        <Drawer
          anchor={'right'}
          open={useDebounce(openClientUserProfileDrawer, 500)}
          onClose={() => {
            setOpenClientUserProfileDrawer(false)
          }}
          PaperProps={{ sx: { width: '500px' } }}
        >
          <AddUserToProfile
            clientUsers={clientUsers}
            userProfiles={userProfiles}
            successCallback={() => {
              setOpenClientUserProfileDrawer(false)
              refetch()
            }}
          />
        </Drawer>

        <Snackbar
          open={showAlert}
          autoHideDuration={6000}
          onClose={() => setShowAlert(false)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert
            onClose={() => setShowAlert(false)}
            severity={alertType}
            sx={{ width: '100%' }}
          >
            {alertMessage}
          </Alert>
        </Snackbar>
      </Grid>
    </Box>
  )
}
export default UserProfile
